import { useState } from "react";
import { downloadAdminExport } from "../../lib/api";
import { Download, FileJson, FileSpreadsheet, Rss, Loader2, ShieldCheck } from "lucide-react";

function ExportRow({ icon: Icon, title, description, path, filename, testId }) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try {
      await downloadAdminExport(path, filename);
    } catch (e) {
      alert("Export failed. Please retry.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex items-start justify-between gap-4 p-5 border-b border-black/5 last:border-0" data-testid={testId}>
      <div className="flex gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-forest/10 text-forest grid place-items-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-forest">{title}</div>
          <div className="text-xs text-charcoal-soft mt-0.5">{description}</div>
        </div>
      </div>
      <button onClick={handle} disabled={loading} className="btn-secondary text-xs px-3 py-2 shrink-0" data-testid={`${testId}-button`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        Download
      </button>
    </div>
  );
}

export default function Exports() {
  return (
    <div data-testid="exports-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-display font-bold text-forest">Exports & Migration</h1>
        <p className="text-charcoal-soft text-sm mt-1 max-w-2xl">
          Download every byte of content this site holds. CSV files open in Excel/Sheets. JSON
          preserves every field exactly — use it to back up, audit, or migrate to another platform.
          You own your content.
        </p>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="ownership-notice">
        <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          <strong>Content ownership.</strong> All blog posts, topic ideas, city pages, accident type
          pages, FAQs, and leads belong to you. These exports are not gated, throttled, or filtered.
          You can move to any other CMS at any time using the JSON export below.
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden mb-6">
        <ExportRow
          icon={FileJson}
          title="Full Site Export (JSON)"
          description="Every collection — blog posts, cities, accident types, FAQs, testimonials, topic ideas, leads — in a single JSON file. Use this for backups or migration to another CMS."
          path=""
          filename="indiana-accident-aid-full-export.json"
          testId="export-full-json"
        />
        <ExportRow
          icon={FileSpreadsheet}
          title="Blog Posts (CSV)"
          description="All blog posts (drafts + scheduled + published) with editorial status, SEO fields, and full content. Opens in Excel / Google Sheets."
          path="/blog-posts.csv"
          filename="blog-posts.csv"
          testId="export-blog-csv"
        />
        <ExportRow
          icon={FileJson}
          title="Blog Posts (JSON)"
          description="All blog posts with every field preserved (lists, FAQs, scheduling)."
          path="/blog-posts.json"
          filename="blog-posts.json"
          testId="export-blog-json"
        />
        <ExportRow
          icon={FileSpreadsheet}
          title="Topic Ideas (CSV)"
          description="The full 120-topic SEO pipeline with cluster, intent, difficulty, status."
          path="/topic-ideas.csv"
          filename="topic-ideas.csv"
          testId="export-ideas-csv"
        />
        <ExportRow
          icon={FileJson}
          title="Topic Ideas (JSON)"
          description="Full topic ideas in JSON for backup or import elsewhere."
          path="/topic-ideas.json"
          filename="topic-ideas.json"
          testId="export-ideas-json"
        />
        <ExportRow
          icon={FileSpreadsheet}
          title="SEO Metadata Inventory (CSV)"
          description="One row per public URL — blog, city, accident-type — with SEO title, description, canonical, status. Perfect for SEO audits."
          path="/seo-metadata.csv"
          filename="seo-metadata.csv"
          testId="export-seo-csv"
        />
      </div>

      <div className="bg-white rounded-2xl border border-black/5 p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 text-terracotta grid place-items-center">
            <Rss className="w-4 h-4" />
          </div>
          <h2 className="font-display font-bold text-forest">RSS Feed</h2>
        </div>
        <p className="text-sm text-charcoal-soft mb-3">
          Your published blog posts are syndicated automatically. Submit this URL to feed readers,
          Google FeedBurner, Mailchimp, Zapier, etc.
        </p>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/rss.xml`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-forest underline break-all"
          data-testid="rss-link"
        >
          {process.env.REACT_APP_BACKEND_URL}/api/rss.xml
        </a>
      </div>
    </div>
  );
}
